import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { chunkLoadTracker } from './chunkLoadTracker';
import { ProgressBar, LoadingAnnouncement, ProgressPhase } from './topProgressBar.styles';

/** Wait this long before showing the bar, so fast navigations never flash it. */
const SHOW_DELAY_MS = 120;
/** Keep the completed bar on screen briefly so the fill + fade-out reads. */
const DONE_LINGER_MS = 350;

/**
 * A thin progress bar pinned to the top of the viewport while a route chunk
 * loads. It appears only if a load runs longer than {@link SHOW_DELAY_MS},
 * trickles toward 90%, then snaps to 100% and fades out when the load resolves.
 * Rendered once, globally (mounted in the app's root layout).
 */
export const TopProgressBar: React.FC = () => {
  const isActive = useSyncExternalStore(
    chunkLoadTracker.subscribe,
    chunkLoadTracker.getIsLoading,
    chunkLoadTracker.getIsLoading,
  );
  const [phase, setPhase] = useState<ProgressPhase | 'idle'>('idle');
  const showTimer = useRef<number | undefined>(undefined);
  const doneTimer = useRef<number | undefined>(undefined);
  const enterFrame = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(showTimer.current);
    window.clearTimeout(doneTimer.current);
    if (enterFrame.current) {
      window.cancelAnimationFrame(enterFrame.current);
    }

    if (isActive) {
      showTimer.current = window.setTimeout(() => {
        // Mount at 0 width, then flip to the trickle on the next frame so the
        // width transition animates in from the left instead of snapping.
        setPhase('enter');
        enterFrame.current = window.requestAnimationFrame(() => setPhase('loading'));
      }, SHOW_DELAY_MS);
    } else {
      setPhase((prev) => {
        if (prev === 'enter' || prev === 'loading') {
          doneTimer.current = window.setTimeout(() => setPhase('idle'), DONE_LINGER_MS);
          return 'done';
        }
        return 'idle';
      });
    }

    return () => {
      window.clearTimeout(showTimer.current);
      window.clearTimeout(doneTimer.current);
      if (enterFrame.current) {
        window.cancelAnimationFrame(enterFrame.current);
      }
    };
  }, [isActive]);

  if (phase === 'idle') {
    return null;
  }

  return createPortal(
    <>
      <ProgressBar $phase={phase} data-testid="route-progress-bar" aria-hidden="true" />
      <LoadingAnnouncement role="status" aria-live="polite">
        {phase !== 'done' ? 'Loading page' : ''}
      </LoadingAnnouncement>
    </>,
    document.body,
  );
};
